import {
  messageKind,
  type ContractBroadcast,
  type ContractResponse,
  type MessagePortLike,
} from "./protocol.js";

/**
 * The page's end of the contract: the three call shapes, over a port.
 *
 * This is the web's preload script. The desktop's does the same job in the same
 * shape against `ipcRenderer`, and the resemblance is deliberate — it is the
 * same contract, so a method that looks different here should be suspected of
 * having changed it.
 *
 * The one thing a port needs that Electron's IPC does not is correlation: an
 * `invoke` has a reply channel of its own, a `postMessage` does not, so every
 * request carries an id and the pending calls are held until an answer with that
 * id arrives.
 */
export class ContractClient {
  readonly #port: MessagePortLike;
  readonly #pending = new Map<
    number,
    { resolve: (value: unknown) => void; reject: (error: Error) => void }
  >();
  /** Subscribers per broadcast channel. A Set, so the same listener subscribed
   * twice is two subscriptions and unsubscribing one leaves the other — which is
   * what two mounted components sharing a channel need. */
  readonly #subscribers = new Map<string, Set<(payload: never) => void>>();
  #nextId = 1;
  #closed = false;

  readonly #receive = (event: { data: unknown }): void => {
    this.#handle(event.data);
  };

  constructor(port: MessagePortLike) {
    this.#port = port;
    port.addEventListener("message", this.#receive);
    port.start?.();
  }

  /** `invoke`: a call with an answer. Rejects with whatever the core said went
   * wrong, and rejects rather than hanging if the port is disposed first. */
  async request<T>(channel: string, ...args: unknown[]): Promise<T> {
    if (this.#closed) throw new Error(`cuekards: the core worker is closed`);
    const id = this.#nextId++;
    const answer = new Promise<unknown>((resolve, reject) => {
      this.#pending.set(id, { resolve, reject });
    });
    this.#port.postMessage({ kind: "request", id, channel, args });
    return (await answer) as T;
  }

  /** `send`: a call with no answer, and no way to refuse it. */
  send(channel: string, ...args: unknown[]): void {
    if (this.#closed) return;
    this.#port.postMessage({ kind: "send", channel, args });
  }

  /** Subscribe to a broadcast channel; returns the unsubscribe the contract's
   * `on…` methods are all documented to return. */
  subscribe<T>(channel: string, listener: (payload: T) => void): () => void {
    const listeners =
      this.#subscribers.get(channel) ?? new Set<(payload: never) => void>();
    this.#subscribers.set(channel, listeners);
    const entry = listener as (payload: never) => void;
    listeners.add(entry);
    return () => {
      listeners.delete(entry);
    };
  }

  /**
   * Stop listening and fail everything in flight.
   *
   * A request whose answer can never arrive has to reject rather than sit there:
   * every one of them is a component's mount effect waiting to draw something,
   * and a prompter that never says why it is blank is worse than one that says
   * it broke.
   */
  dispose(): void {
    if (this.#closed) return;
    this.#closed = true;
    this.#port.removeEventListener("message", this.#receive);
    const pending = [...this.#pending.values()];
    this.#pending.clear();
    this.#subscribers.clear();
    for (const { reject } of pending) {
      reject(new Error("cuekards: the core worker was closed"));
    }
  }

  #handle(data: unknown): void {
    switch (messageKind(data)) {
      case "response":
        this.#answer(data as ContractResponse);
        return;
      case "broadcast":
        this.#publish(data as ContractBroadcast);
        return;
      // Anything else on this port belongs to someone else — the settings write
      // the page persists is the one that does today.
      default:
        return;
    }
  }

  #answer(response: ContractResponse): void {
    const pending = this.#pending.get(response.id);
    if (!pending) return;
    this.#pending.delete(response.id);
    if (response.ok) pending.resolve(response.value);
    else pending.reject(new Error(response.message));
  }

  #publish(broadcast: ContractBroadcast): void {
    const listeners = this.#subscribers.get(broadcast.channel);
    if (!listeners) return;
    // Copied, so a listener that unsubscribes itself while being called does not
    // disturb the iteration it is being called from.
    for (const listener of [...listeners]) {
      (listener as (payload: unknown) => void)(broadcast.payload);
    }
  }
}
